import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SearchProvider, useSearch } from '../providers/SearchProvider';
import { ReviewSearchModal } from './ReviewSearchModal';
import type { RootState } from '../store';
import { navigateToReviewSearchResult } from '../navigation/navigationRef';
import {
  toNavigableReviewItemId,
  type ReviewSearchResult,
} from '../utils/reviewItemSearch';

// Internal component that uses the search context
const SearchModalWrapper: React.FC = () => {
  const {
    isSearchModalVisible,
    hideSearchModal,
    currentReviewId,
    navigationContext,
    onItemSelect,
  } = useSearch();
  const reviewShell = useSelector((state: RootState) => {
    if (!currentReviewId) {
      return null;
    }

    // Prefer the fully hydrated active review over the shallow list entry.
    // The list entry is enough to identify a review, but it does not carry the
    // historical/template inventory required for local-first offline search.
    return Number(state.review.activeReview?.id) === Number(currentReviewId)
      ? state.review.activeReview
      : state.review.reviews.find(candidate =>
        Number(candidate.id) === Number(currentReviewId)
      ) || null;
  });
  const reviewTemplateId = reviewShell?.templateId ??
    reviewShell?.template?.id ??
    (reviewShell as any)?.reviewData?.template?.id;
  const hydratedTemplate = useSelector((state: RootState) =>
    reviewTemplateId == null
      ? null
      : state.review.templates.find(template =>
        Number(template.id) === Number(reviewTemplateId)
      ) || null
  );
  const review = useMemo(() => {
    if (!reviewShell) {
      return null;
    }

    const embeddedCategorySets = [
      reviewShell.template?.categories,
      (reviewShell as any).categories,
      (reviewShell as any).reviewData?.template?.categories,
    ];
    if (embeddedCategorySets.some(categories =>
      Array.isArray(categories) && categories.length > 0
    )) {
      return reviewShell;
    }

    // ReviewDetail can render a shell review with the separately hydrated
    // template store. Give global header search that same inventory instead of
    // falling back to an offline API request with an empty review shell.
    if (!Array.isArray(hydratedTemplate?.categories)) {
      return reviewShell;
    }

    return {
      ...reviewShell,
      template: {
        ...hydratedTemplate,
        ...(reviewShell.template || {}),
        categories: hydratedTemplate.categories,
      },
    };
  }, [hydratedTemplate, reviewShell]);
  const additionalItems = useSelector((state: RootState) =>
    currentReviewId ? state.review.onDemandItems?.[currentReviewId] || [] : []
  );

  // Only render modal if we have a reviewId
  if (!currentReviewId) {
    return null;
  }

  const handleItemSelect = (item: ReviewSearchResult) => {
    if (onItemSelect) {
      onItemSelect(item);
      return;
    }

    const itemId = toNavigableReviewItemId(item.id);
    if (itemId === null) {
      console.error('[ReviewSearch] Search result has an invalid item ID', item.id);
      return;
    }

    navigateToReviewSearchResult(
      navigationContext,
      currentReviewId,
      itemId,
      item.categoryName || item.category?.name || 'Item'
    );
  };

  return (
    <ReviewSearchModal
      visible={isSearchModalVisible}
      onDismiss={hideSearchModal}
      reviewId={currentReviewId}
      onItemSelect={handleItemSelect}
      review={review}
      additionalItems={additionalItems}
    />
  );
};

// Main component that provides the context and modal
export const ReviewSearch: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <SearchProvider>
      {children}
      <SearchModalWrapper />
    </SearchProvider>
  );
};
